import React from 'react';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import type { i18n } from 'i18next';
import { IAppState } from '../redux/store/types';
import CollapsibleText from './CollapsibleText';
import { FileUploadButton } from './FileUploadButton';
import { IRecord, ViewMode } from '../redux/records/types';
import { ISubrecord, INewSubrecord } from '../redux/subrecords/types';
import { timeShortDisplay, USER_TEXT_MAX_LENGTH } from '../utils/utils';
import {
  prepareReadSubrecordsCommandAction,
  prepareCreateSubrecordCommandAction,
  addSubrecordAction,
  prepareDeleteSubrecordCommandAction,
  deleteSubrecordAction,
} from '../redux/subrecords/actions';
import { setViewModeAction, setRecordStateAction } from '../redux/records/actions';
import { SendHorizontal, Trash2, Undo2, ArrowUp } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import { UserType } from 'redux/auth/types';
import { ISaasTenant } from 'redux/saasTenants/types';
import { toast } from 'react-toastify';

// Type-safe field names for ISubrecord
type SubrecordField = keyof Pick<ISubrecord, 'user_text' | 'ai_text'>;

/**
 * Component for managing and displaying subrecords.
 * @class Subrecords
 * @extends React.Component<ISubrecordProps, { showEmptySubrecord: boolean, emptySubrecord: ISubrecord }>
 */
class Subrecords extends React.Component<ISubrecordProps, { showEmptySubrecord: boolean; emptySubrecord: INewSubrecord; uploadedFiles: string[] }> {
  private textareaRef = React.createRef<HTMLTextAreaElement>();

  /**
   * Creates an initial subrecord with default values
   * @returns {ISubrecord} A new subrecord with default values
   */
  private createInitialSubrecord = (): INewSubrecord => ({
    subrecord_id: uuidv4(),
    record_id: this.props.currentRecord.record_id,
    user_text: '',
    non_ai: false,
  });

  constructor(props: ISubrecordProps) {
    super(props);
    this.state = {
      showEmptySubrecord: true,
      emptySubrecord: this.createInitialSubrecord(),
      uploadedFiles: [],
    };
  }

  /**
   * Lifecycle method
   */
  componentDidMount() {
    this.props.prepareReadSubrecordsCommandAction(this.props.currentRecord.record_id);
    if (this.props.viewMode !== ViewMode.SHOW_SEARCH) setTimeout(() => this.focusInputBox(), 500);
  }

  /**
   * Updates component when props change
   * @param {ISubrecordProps} prevProps - Previous component props
   */
  componentDidUpdate(prevProps: ISubrecordProps) {
    if (prevProps.currentRecord?.record_id !== this.props.currentRecord.record_id) {
      this.props.prepareReadSubrecordsCommandAction(this.props.currentRecord.record_id);
      // Update emptySubrecord's record_id when currentRecord changes
      this.setState((prevState) => ({
        showEmptySubrecord: prevState.showEmptySubrecord,
        emptySubrecord: { ...prevState.emptySubrecord, record_id: this.props.currentRecord?.record_id || '' },
      }));
    } else if (this.props.viewMode !== prevProps.viewMode && this.props.viewMode !== ViewMode.SHOW_SEARCH) {
      setTimeout(() => this.focusInputBox(), 500);
    }
  }

  render() {
    const { subrecords, t, showAllLinkable, viewMode } = this.props;

    return (
      <div className='page body-container subrecords-container'>
        <div className='data-container subrecords-list'>
          {this.state.showEmptySubrecord && <div className='table-row subrecords input'>{this.renderSubrecord(this.state.emptySubrecord)}</div>}

          {subrecords.length > 0 &&
            (viewMode === ViewMode.SHOW_EXPANDED_VIEW ? (
              subrecords.map((subrecord, index) => (
                <div key={subrecord.subrecord_id}>
                  <div tabIndex={index} className={`table-row subrecords${subrecord.onroute ? ' onroute' : ''}`}>
                    {this.renderSubrecord(subrecord)}
                  </div>
                  {!subrecord.onroute && !showAllLinkable && (
                    <button key={0} className='action-button to-input' title={t('records.shallWeContinue')} onClick={this.focusInputBox}>
                      <ArrowUp />
                    </button>
                  )}
                </div>
              ))
            ) : (
              <div className='remaining-subrecords-count-container'>
                <div className='table-row subrecords'>{this.renderSubrecord(subrecords[0])}</div>
                {subrecords.length > 1 && (
                  <span
                    className='table-row subrecords remaining-subrecords-count link'
                    onClick={() => this.props.setViewModeAction(ViewMode.SHOW_EXPANDED_VIEW)}
                  >
                    + {subrecords.length - 1} {this.props.t('subrecords.title')} ...
                  </span>
                )}
              </div>
            ))}
        </div>
      </div>
    );
  }

  focusInputBox = (): void => {
    if (this.textareaRef.current) {
      this.textareaRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
      this.textareaRef.current.focus();
    }
  };

  /**
   * Validates a subrecord before execution
   * @param subrecord - Subrecords to validate
   * @returns {boolean} Whether the subrecord is valid
   */
  isSubrecordValid = (subrecord: INewSubrecord) => {
    return (
      (subrecord.user_text && subrecord.user_text.length <= USER_TEXT_MAX_LENGTH) ||
      this.state.uploadedFiles.length > 0 ||
      subrecord.consolidateEntireChat ||
      subrecord.replaceChatWithConsolidation
    );
  };

  /**
   * Handles file upload changes
   */
  handleFilesChange = (fileKeys: string[]) => {
    this.setState({ uploadedFiles: fileKeys });
  };

  /**
   * Updates a specific field in the empty subrecord state
   * @param {SubrecordField} field - Field to update
   * @param {string | number | boolean} value - New value for the field
   */
  handleEmptySubrecordChange = (field: SubrecordField, value: string) => {
    this.setState((prevState) => ({
      emptySubrecord: {
        ...prevState.emptySubrecord,
        [field]: value,
      },
    }));
  };

  /**
   * Saves a subrecord and resets the form
   * @param {ISubrecord} subrecord - Subrecords to save
   */
  handleSaveSubrecord = async (subrecord: INewSubrecord) => {
    const { userType, subrecords, t } = this.props;

    if (userType === UserType.Admin && subrecord.user_text.includes('--testing--')) this.generateTestCommands(subrecord);
    else if (this.props.userRemainingCredit <= 0) toast.error(t('tokenUsage.noRemainingCredit'), { autoClose: false });
    else if (!subrecord.replaceChatWithConsolidation) {
      if (subrecord.consolidateEntireChat) {
        subrecord.consolidateEntireChat = t('subrecords.consolidateEntireChatDescription');
        subrecord.user_text = '';
      }
      this.props.addSubrecordAction({
        ...subrecord,
        created_at: new Date().toISOString(),
        ai_text: '',
        onroute: true,
      });
      this.props.prepareCreateSubrecordCommandAction({ ...subrecord, uploadedFiles: this.state.uploadedFiles });
      this.props.setViewModeAction(ViewMode.SHOW_CONDENSED_RECORDS_VIEW);
      this.setState({ emptySubrecord: this.createInitialSubrecord() }, () => setTimeout(() => this.focusInputBox(), 1000)); // Focus the input after the state has been updated and DOM re-rendered
    } else if (subrecords.length > 0) {
      // replaceChatWithConsolidation is only enabled when subrecords.length === 1 && subrecords[0].is_consolidated
      const deleteSubrecordId = subrecords[0].subrecord_id;
      this.props.deleteSubrecordAction(subrecord.record_id, deleteSubrecordId);
      this.props.setRecordStateAction({ record_id: subrecord.record_id, ai_text: '', onroute: true });
      this.props.prepareDeleteSubrecordCommandAction({ record_id: subrecord.record_id, subrecord_id: deleteSubrecordId, replaceChatWithConsolidation: true });
    }

    this.setState({ uploadedFiles: [] });
  };

  /**
   * --testing--10
   */
  generateTestCommands = (subrecord: INewSubrecord) => {
    const testPhrase = '--testing--';
    const testIndex = subrecord.user_text.indexOf(testPhrase);
    const iterationsCount = subrecord.user_text.substring(testIndex + testPhrase.length).trim();

    for (let i = 0; i < Number(iterationsCount); i++) {
      setTimeout(() => {
        const testSubrecord = { ...subrecord, subrecord_id: uuidv4(), user_text: subrecord.user_text + ' ' + i, non_ai: true };
        this.props.addSubrecordAction({ ...testSubrecord, created_at: new Date().toISOString(), ai_text: '', onroute: true });
        this.props.prepareCreateSubrecordCommandAction(testSubrecord);
        if (i === Number(iterationsCount) - 1) {
          // Only reset form on the last subrecord
          this.setState({ emptySubrecord: this.createInitialSubrecord() });
        }
      }, i * 100); // delay between each call (TODO: Check rate limit)
    }
  };

  handleDeleteSubrecord = (subrecord_id: string, subrecord_user_text: string) => {
    const { t } = this.props;
    if (window.confirm(t('subrecords.confirmDelete', { description: subrecord_user_text }))) {
      const record_id = this.props.currentRecord.record_id;
      this.props.prepareDeleteSubrecordCommandAction({ record_id, subrecord_id });
      this.props.deleteSubrecordAction(record_id, subrecord_id);
    }
  };

  /**
   * Cancels the current subrecord and resets the form
   */
  handleCancelSubrecord = () => {
    if (window.confirm(this.props.t('common.confirmUnsavedChanges'))) {
      this.setState({
        emptySubrecord: this.createInitialSubrecord(),
      });
    }
  };

  /**
   * Renders a single subrecord row
   * @param {Partial<ISubrecord>} subrecord - Subrecords to render
   * @returns {JSX.Element} The rendered subrecord row
   */
  renderSubrecord = (subrecord: Partial<ISubrecord>) => {
    const { t, currentRecord, subrecords, userId, showAllLinkable } = this.props;

    const isSaved = !!subrecord.created_at;
    const isValid = !isSaved ? this.isSubrecordValid(subrecord as INewSubrecord) : true;
    const onroute = subrecords[0]?.onroute || currentRecord.onroute;
    const isLastSubrecordConsolidated = subrecords.length === 1 && subrecords[0].is_consolidated;

    return (
      <>
        {isSaved && (
          <div className='updated-at'>
            <span>{timeShortDisplay(new Date(subrecord.created_at!))}</span>
            <span className='saas-user-name'>{subrecord.saas_tenant_name}</span>
            <div className='actions'>
              {!subrecord.onroute && subrecord.saas_tenant_id === userId && (
                <button
                  onClick={() => this.handleDeleteSubrecord(subrecord.subrecord_id as string, subrecord.user_text as string)}
                  className='action-button delete'
                  title={t('common.delete')}
                >
                  <Trash2 />
                </button>
              )}
            </div>
          </div>
        )}

        <div className={!isSaved ? 'required' : ''}>
          {isSaved ? (
            <CollapsibleText
              ownerRecordId={subrecord.record_id as string}
              ownerSubrecordId={subrecord.subrecord_id as string}
              userText={subrecord.user_text || ''}
              aiText={subrecord.ai_text || ''}
              interim={subrecord.interim}
              non_ai={subrecord.non_ai}
            />
          ) : (
            !onroute &&
            !showAllLinkable && (
              <>
                <div className='textarea-container'>
                  <textarea
                    ref={this.textareaRef}
                    rows={3}
                    placeholder={t('records.shallWeContinue')}
                    className={`user-text ${onroute ? 'onroute' : ''} ${this.state.emptySubrecord.consolidateEntireChat ? 'consolidate-entire-chat' : ''}`}
                    value={subrecord.user_text || ''}
                    onChange={(e) => {
                      if (!isSaved) this.handleEmptySubrecordChange('user_text' as SubrecordField, e.target.value);
                    }}
                    readOnly={isSaved}
                    disabled={!!this.state.emptySubrecord.consolidateEntireChat}
                  />
                  {!isSaved && !this.state.emptySubrecord.consolidateEntireChat && (
                    <FileUploadButton JWT={this.props.JWT} recordId={this.props.currentRecord.record_id} onTextualFilesChange={this.handleFilesChange} />
                  )}
                </div>

                <div className='checkbox-container'>
                  <label title={t('subrecords.nonAIDescription')}>
                    <input
                      type='checkbox'
                      checked={this.state.emptySubrecord.non_ai || false}
                      onChange={(e) => {
                        this.setState((prevState) => ({
                          ...prevState,
                          emptySubrecord: {
                            ...prevState.emptySubrecord,
                            non_ai: e.target.checked,
                          },
                        }));
                      }}
                      disabled={!!this.state.emptySubrecord.consolidateEntireChat || this.state.emptySubrecord.replaceChatWithConsolidation}
                    />
                    {t('subrecords.nonAICaption')}
                  </label>
                  {isLastSubrecordConsolidated ? (
                    <label title={t('subrecords.replaceChatWithConsolidationDescription')}>
                      <input
                        type='checkbox'
                        checked={!!this.state.emptySubrecord.replaceChatWithConsolidation || false}
                        onChange={(e) => {
                          this.setState((prevState) => ({
                            ...prevState,
                            emptySubrecord: {
                              ...prevState.emptySubrecord,
                              replaceChatWithConsolidation: e.target.checked,
                            },
                          }));
                        }}
                        disabled={currentRecord.saas_tenant_id !== userId || this.state.emptySubrecord.non_ai}
                      />
                      {t('subrecords.replaceChatWithConsolidationCaption')}
                    </label>
                  ) : (
                    <label title={t('subrecords.onlyByChatCreator')}>
                      <input
                        type='checkbox'
                        checked={!!this.state.emptySubrecord.consolidateEntireChat || false}
                        onChange={(e) => {
                          this.setState((prevState) => ({
                            ...prevState,
                            emptySubrecord: {
                              ...prevState.emptySubrecord,
                              consolidateEntireChat: e.target.checked ? 'T' : '',
                            },
                          }));
                        }}
                        disabled={currentRecord.saas_tenant_id !== userId || this.state.emptySubrecord.non_ai}
                      />
                      {t('subrecords.consolidateEntireChatCaption')}
                    </label>
                  )}
                </div>
              </>
            )
          )}
        </div>

        {!isSaved &&
          (this.state.emptySubrecord.user_text ||
            this.state.emptySubrecord.consolidateEntireChat ||
            this.state.emptySubrecord.replaceChatWithConsolidation ||
            this.state.uploadedFiles.length > 0) && (
            <div className='actions has-value'>
              <button
                onClick={() => this.handleSaveSubrecord(subrecord as INewSubrecord)}
                className='action-button save'
                title={t('common.send')}
                disabled={!isValid}
              >
                <SendHorizontal />
              </button>
              <button onClick={() => this.handleCancelSubrecord()} className='action-button cancel' title={t('common.cancel')}>
                <Undo2 />
              </button>
            </div>
          )}
      </>
    );
  };
}

/**
 * Props interface for Subrecords component
 * @interface ISubrecordProps
 */
interface ISubrecordProps {
  t: (key: string, options?: any) => string;
  i18n: i18n;
  tReady: boolean;
  userId: string;
  userType: UserType;
  JWT: string | null;
  userRemainingCredit: number;
  subrecords: ISubrecord[]; // List of subrecords
  showAllLinkable: boolean;
  viewMode: ViewMode;
  setViewModeAction: typeof setViewModeAction;
  setRecordStateAction: typeof setRecordStateAction;
  records: IRecord[]; // List of all records
  currentRecord: IRecord; // Currently selected record
  prepareReadSubrecordsCommandAction: typeof prepareReadSubrecordsCommandAction;
  prepareCreateSubrecordCommandAction: typeof prepareCreateSubrecordCommandAction;
  addSubrecordAction: typeof addSubrecordAction;
  prepareDeleteSubrecordCommandAction: typeof prepareDeleteSubrecordCommandAction;
  deleteSubrecordAction: typeof deleteSubrecordAction;
}

/**
 * Maps Redux state to component props
 * @param {IAppState} state - Current Redux state
 * @returns {Partial<ISubrecordProps>} Props derived from state
 */
const mapStateToProps = (state: IAppState) => {
  const currentSaasTenant = state.saasTenants.saasTenants.find((tenant) => tenant.saas_tenant_id === state.auth.userId) as ISaasTenant;
  return {
    userId: state.auth.userId as string,
    userType: state.auth.userType,
    JWT: state.auth.JWT,
    userRemainingCredit: (currentSaasTenant && (currentSaasTenant.credit as number) - ((currentSaasTenant.usage?.total_cost as number) || 0)) || 0,
    subrecords: state.subrecords.subrecords[state.records.currentRecordId as string] || [],
    records: state.records.records,
    showAllLinkable: state.records.showAllLinkable,
    currentRecord: state.records.records.find((acc) => acc.record_id === state.records.currentRecordId) as IRecord,
    viewMode: state.records.viewMode,
  };
};

// Map Redux actions to component props
const mapDispatchToProps = {
  prepareReadSubrecordsCommandAction,
  prepareCreateSubrecordCommandAction,
  addSubrecordAction,
  prepareDeleteSubrecordCommandAction,
  deleteSubrecordAction,
  setViewModeAction,
  setRecordStateAction,
};

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(Subrecords));
